import React from 'react';
import { type DocumentElement as DocumentElementType } from '@eaco/forms-core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  findIconDefinition,
  type IconName,
  type IconPrefix,
} from '@fortawesome/fontawesome-svg-core';
import { cn } from '../lib/utils';
import { useElementValue, useIsReadOnly } from '../DocumentView/DocumentContext';
import HiddenElementBanner from './HiddenElementBanner';
import ArchivedElementBanner from './ArchivedElementBanner';
import { useDefaultValue } from './useDefaultValue';
import { FieldHeader, FieldHelpText } from './FieldParts';

/**
 * Resolve a FontAwesome icon by name across the prefixes the host app
 * has loaded into the library. Falls back to a regular-style guess so
 * configForms can ship icons with a bare name (e.g. "chart-pie") and
 * still render whichever prefix the host registers.
 */
const resolveIcon = (icon: unknown) => {
  if (typeof icon !== 'string' || icon === '') return null;
  for (const prefix of ['far', 'fas', 'fal', 'fab'] as IconPrefix[]) {
    const found = findIconDefinition({
      prefix,
      iconName: icon as IconName,
    });
    if (found) return found;
  }
  return null;
};

interface Props {
  element: DocumentElementType;
}

interface RadioCardOption {
  value: string;
  label: string;
  description?: string;
  /**
   * Optional FontAwesome icon name. Currently used only by system
   * configForms (e.g. the Score variant picker) — the regular
   * radio-cards options editor doesn't expose this field, but the
   * renderer reads it whenever it's set.
   */
  icon?: string;
}

/**
 * Per-pick snapshot — matches the dropdown rich-entry pattern so an
 * answer keeps its meaning even if the option was edited or removed
 * after submission. Always a single-element array (radio = single
 * select) for shape parity with dropdown's allowMultiple results.
 */
interface RadioCardsRichEntry {
  value: string;
  label: string;
  description: string | null;
}

const isRichEntry = (v: unknown): v is RadioCardsRichEntry =>
  v !== null &&
  typeof v === 'object' &&
  !Array.isArray(v) &&
  typeof (v as RadioCardsRichEntry).value === 'string';

const toRichEntries = (raw: unknown): RadioCardsRichEntry[] => {
  if (Array.isArray(raw)) return raw.filter(isRichEntry);
  if (typeof raw === 'string' && raw !== '') {
    return [{ value: raw, label: raw, description: null }];
  }
  return [];
};

const richEntryFromOption = (opt: RadioCardOption): RadioCardsRichEntry => ({
  value: opt.value,
  label: opt.label,
  description: opt.description ?? null,
});

const RadioCards: React.FC<Props> = ({ element }) => {
  const [rawValue, setRawValue] = useElementValue<unknown>(element.id);
  const isReadOnly = useIsReadOnly();
  const data = element.data as {
    label?: string;
    subLabel?: string;
    required?: boolean;
    helpText?: string;
    hidden?: boolean;
    defaultValue?: string;
    options?: RadioCardOption[];
    /**
     * When true (default), clicking the already-selected card clears
     * the answer back to empty. Set to false when the field must always
     * hold a value (e.g. a mode picker with a sensible default).
     */
    allowDeselection?: boolean;
  };

  const label = data?.label || 'Choose one';
  const subLabel = data?.subLabel;
  const required = data?.required || false;
  const helpText = data?.helpText;
  // Defaults to true so the existing radio-cards instances keep their
  // toggle-style behaviour. Set to false on the dropdown's scoringMode
  // picker — the form needs a value there at all times.
  const allowDeselection = data?.allowDeselection !== false;
  // Skip fully-blank options — the editor persists in-progress rows
  // with empty value+label so designers can type into them without
  // them vanishing.
  const options = (Array.isArray(data?.options) ? data.options : []).filter(
    (o) => (o?.value ?? '').trim() !== '' || (o?.label ?? '').trim() !== '',
  );

  const richEntries = React.useMemo(() => toRichEntries(rawValue), [rawValue]);
  // Pick-or-undefined for the hook (so the seed effect doesn't see an
  // empty string as "already filled" and bail), and the always-string
  // version for Radix render props.
  const pickedValue = richEntries[0]?.value;
  const currentValue = pickedValue ?? '';

  // Seed the default once when the field is otherwise empty.
  // When deselection is locked off and the designer didn't pick an
  // explicit default, fall back to the first option so the field
  // always carries a value.
  const effectiveDefault =
    typeof data?.defaultValue === 'string' && data.defaultValue !== ''
      ? data.defaultValue
      : !allowDeselection && options.length > 0
      ? options[0].value
      : undefined;
  useDefaultValue(
    pickedValue,
    (next) => {
      const opt = options.find((o) => o.value === next);
      if (opt) setRawValue([richEntryFromOption(opt)]);
    },
    effectiveDefault,
  );

  const handleChange = (next: string) => {
    const opt = options.find((o) => o.value === next);
    if (!opt) return;
    setRawValue([richEntryFromOption(opt)]);
  };

  // Read-only (e.g. a completed result): render only the selected
  // option as a static, highlighted card — no radios, no click
  // handlers. Prefers the snapshotted rich-entry label/description so a
  // card edited or removed after submission still shows its picked text.
  if (isReadOnly) {
    const picked = richEntries[0];
    const liveOption = picked
      ? options.find((o) => o.value === picked.value)
      : undefined;
    const selectedLabel = picked?.label || liveOption?.label || picked?.value;
    const selectedDescription =
      picked?.description ?? liveOption?.description ?? null;
    const iconDef = liveOption ? resolveIcon(liveOption.icon) : null;
    return (
      <div className="space-y-2">
        <FieldHeader
          htmlFor={element.id}
          label={label}
          subLabel={subLabel}
          required={required}
        />
        {picked ? (
          <div className="flex items-start gap-3 rounded-lg border border-primary bg-background p-3 text-sm shadow-sm ring-1 ring-primary">
            {iconDef && (
              <FontAwesomeIcon
                icon={iconDef}
                className="mt-0.5 h-4 w-4 flex-shrink-0 text-primary"
              />
            )}
            <div className="flex flex-col gap-0.5">
              <span className="font-medium leading-tight">{selectedLabel}</span>
              {selectedDescription && (
                <span className="text-xs text-muted-foreground">
                  {selectedDescription}
                </span>
              )}
            </div>
          </div>
        ) : (
          <p className="text-sm text-muted-foreground">—</p>
        )}
        <FieldHelpText>{helpText}</FieldHelpText>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <FieldHeader
        htmlFor={element.id}
        label={label}
        subLabel={subLabel}
        required={required}
      />

      {options.length === 0 ? (
        <p className="rounded-md border border-dashed border-border bg-muted/20 px-3 py-4 text-center text-xs text-muted-foreground">
          No options yet. Add some in the element configuration.
        </p>
      ) : (
        <div id={element.id} className="grid grid-cols-1 gap-[10px]">
          {options.map((opt) => {
            const checked = opt.value === currentValue;
            const iconDef = resolveIcon(opt.icon);
            return (
              <button
                key={opt.value}
                type="button"
                role="radio"
                aria-checked={checked}
                onClick={() => {
                  // Opt-in deselection: re-clicking the selected card clears
                  // the answer when allowDeselection is on (the default).
                  if (checked) {
                    if (allowDeselection) setRawValue([]);
                  } else {
                    handleChange(opt.value);
                  }
                }}
                className={cn(
                  'flex cursor-pointer flex-col gap-[6px] rounded-[12px] p-[13px] text-left transition-colors',
                  checked
                    ? 'border-[1.5px] border-[var(--forms-accent,var(--forms-accent-default))] bg-[var(--forms-input-filled-bg)]'
                    : 'border-[length:var(--forms-input-border-w)] border-[var(--forms-border)] bg-white',
                )}
              >
                <div className="flex items-center justify-between">
                  <span
                    className={cn(
                      'inline-flex',
                      checked
                        ? 'text-[var(--forms-accent,var(--forms-accent-default))]'
                        : 'text-[var(--forms-optional)]',
                    )}
                  >
                    {iconDef ? (
                      <FontAwesomeIcon icon={iconDef} style={{ fontSize: 19 }} />
                    ) : null}
                  </span>
                  <span
                    className="h-[18px] w-[18px] flex-none rounded-full bg-white"
                    style={
                      checked
                        ? {
                            border:
                              '5px solid var(--forms-accent, var(--forms-accent-default))',
                          }
                        : { border: '1.6px solid #cdd3db' }
                    }
                  />
                </div>
                <span className="text-[13.5px] font-bold text-[var(--forms-ink)]">
                  {opt.label}
                </span>
                {opt.description && (
                  <span className="text-[11.5px] text-[var(--forms-muted-soft)]">
                    {opt.description}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      )}

      <FieldHelpText>{helpText}</FieldHelpText>
      <HiddenElementBanner hidden={data?.hidden} />
      <ArchivedElementBanner archived={element.archived} />
    </div>
  );
};

export default RadioCards;
